import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Box,
  Divider,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import {
  ControlPosition,
  MapControl,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { ChevronLeft } from 'lucide-react';
import moment from 'moment';
import { useDebouncedCallback } from 'use-debounce';

import { MapLayerApi } from '@/api';
import {
  IcCalendar,
  IcPersonCreated,
  IcOpenManholePin,
  IcFireHydrantPin,
  IcRoadBlockPin,
  IcFloodingPin,
  IcLimitedVisibilityPin,
  IcPotHolePin,
  IcDeadAnimalPin,
  IcRoadWorkPin,
  IcDownWiresPin,
  IcDownPolePin,
  IcDebrisPin,
  IcArrowLeft,
  IcLocationMarker,
  IcEdit,
} from '@/assets/images';
import { Page } from '@/components';
import { GoogleMap, IMarker } from '@/components/GoogleMap';
import FireHydrantPopup from '@/components/GoogleMap/FireHydrantPopup';
import { MapTypeControl } from '@/components/GoogleMap/MapTypeControl';
import CreateNewMarkerModal from '@/components/Modals/CreateNewMarkerModal/CreateNewMarkerModal';
import { withLoader } from '@/hocs';
import { useMarkerTypes } from '@/hooks/useMarkerTypes';
import {
  IPointLayer,
  PointLayerType,
  PointLayerTypeLabel,
  Role,
} from '@/models';
import {
  ILocationMarkerFilter,
  LocationMarkerFilterMenu,
} from '@/pages/LocationMarkers/components/LocationMarkersFilter';
// import { useStyles } from '@/pages/LocationMarkers/sharedStyles';
import { googlePlaceService, isTargetRole } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils';

const enum TABS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

function isExpired(time?: number) {
  if (!time) {
    return false;
  }
  return time < moment().unix();
}

function formatUnixTimestamp(timestamp: number, formatString: string) {
  /* eslint-disable-next-line import/no-named-as-default-member */
  return moment.unix(timestamp).format(formatString);
}

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '1.1rem',
  color: `${theme.palette.text.secondary} !important`,
  height: '75px',
  '&.Mui-selected': {
    color: `${theme.palette.text.primary} !important`,
  },
  '& .MuiButtonBase-root': {
    padding: '20px',
  },
}));

const TABS_BY_PAGE = [
  { label: 'Active', path: 'ACTIVE' },
  { label: 'Inactive', path: 'INACTIVE' },
];

const initialFilterValues: ILocationMarkerFilter = {
  createdBy: '',
  markerName: '',
  from: '',
  to: '',
  hasExpiration: null,
};

const LocationMarkers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkMarkerId = searchParams.get('markerId');
  const { user } = useUserStore();
  const theme = useTheme();
  const { enabledMarkerTypes } = useMarkerTypes();
  const [selectedTab, setSelectedTab] = useState(
    deepLinkMarkerId ? TABS.INACTIVE : TABS.ACTIVE,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [markers, setMarkers] = useState<IMarker[]>([]);
  const [rawMarkers, setRawMarkers] = useState<IPointLayer[]>();
  const [selectedMarker, setSelectedMarker] = useState<IPointLayer | null>(
    null,
  );
  const { updateToast } = useToastStore();
  const [searchValue, setSearchValue] = useState('');
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(
    null,
  );

  const [selectedMarkerKey, setSelectedMarkerKey] = useState<string | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState(initialFilterValues);

  // Infinite scrolling state
  const [allMarkers, setAllMarkers] = useState<IPointLayer[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(20);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);
  const [newMarkerModal, setNewMarkerModal] = useState<boolean>(false);
  const [editMarker, setEditMarker] = useState<IPointLayer | null>(null);
  const [locationMarker, setLocationMarker] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [addressSearchValue, setAddressSearchValue] = useState('');
  const [options, setOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  const defaultMapCenter = {
    lng: -73.983,
    lat: 41.149,
  };

  const map = useMap();

  const resetMapToDefault = useCallback(() => {
    if (map) {
      map.panTo(defaultMapCenter);
      map.setZoom(12);
    }
  }, [map, defaultMapCenter]);

  const getMarkerTypeColor = useCallback(
    (type: string): string => {
      if (typeof type === 'string') {
        const markerType = enabledMarkerTypes.find((mt) => mt.name === type);
        if (markerType && markerType.color) {
          return markerType.color;
        }

        // Fallback to a deterministic color based on the type name
        const fallbackColors = [
          '#FF6B6B',
          '#4ECDC4',
          '#45B7D1',
          '#96CEB4',
          '#FFEAA7',
          '#DDA0DD',
          '#98D8C8',
          '#F7DC6F',
          '#BB8FCE',
          '#85C1E9',
        ];

        let hash = 0;
        for (let i = 0; i < type.length; i++) {
          hash = type.charCodeAt(i) + ((hash << 5) - hash);
        }
        const colorIndex = Math.abs(hash) % fallbackColors.length;
        return fallbackColors[colorIndex];
      }
      return '#FF6B6B'; // Default red color
    },
    [enabledMarkerTypes],
  );

  const getIconForType = useCallback(
    (type: PointLayerType | string, color: string) => {
      switch (type) {
        case PointLayerType.FIRE_HYDRANT:
          return <IcFireHydrantPin />;
        case PointLayerType.ROAD_BLOCK:
          return <IcRoadBlockPin />;
        case PointLayerType.FLOODING:
          return <IcFloodingPin />;
        case PointLayerType.LIMITED_VISIBILITY:
          return <IcLimitedVisibilityPin />;
        case PointLayerType.POTHOLE:
          return <IcPotHolePin />;
        case PointLayerType.DEAD_ANIMAL:
          return <IcDeadAnimalPin />;
        case PointLayerType.ROAD_WORK:
          return <IcRoadWorkPin />;
        case PointLayerType.DOWN_WIRES:
          return <IcDownWiresPin />;
        case PointLayerType.DOWN_POLE:
          return <IcDownPolePin />;
        case PointLayerType.DEBRIS:
          return <IcDebrisPin />;
        case PointLayerType.OPEN_MANHOLE:
          return <IcOpenManholePin />;
      }

      if (typeof type === 'string') {
        switch (type) {
          case 'Fire Hydrant':
            return <IcFireHydrantPin />;
          case 'Road Block':
            return <IcRoadBlockPin />;
          case 'Flooding':
            return <IcFloodingPin />;
          case 'Limited Visibility':
            return <IcLimitedVisibilityPin />;
          case 'Pothole':
            return <IcPotHolePin />;
          case 'Dead Animal':
            return <IcDeadAnimalPin />;
          case 'Road Work':
            return <IcRoadWorkPin />;
          case 'Down Wires':
            return <IcDownWiresPin />;
          case 'Down Pole':
            return <IcDownPolePin />;
          case 'Debris':
            return <IcDebrisPin />;
          case 'Open Manhole':
            return <IcOpenManholePin />;
          default:
            return <IcLocationMarker color={color} />;
        }
      }

      return <IcLocationMarker color={color} />;
    },
    [],
  );

  useEffect(() => {
    if (!deepLinkMarkerId || !map || allMarkers.length === 0) return;

    const found = allMarkers.find((m) => m._id === deepLinkMarkerId);
    if (found?.point?.coordinates) {
      map.setCenter({
        lng: found.point.coordinates[0],
        lat: found.point.coordinates[1],
      });
      map.setZoom(16);
      setSelectedMarker(found);
      setSelectedMarkerKey(found._id as string);
    }

    searchParams.delete('markerId');
    setSearchParams(searchParams, { replace: true });
  }, [allMarkers, deepLinkMarkerId, map, searchParams, setSearchParams]);

  useEffect(() => {
    // Configure the click listener for left-click to add markers
    if (map) {
      map?.addListener(
        'click',
        async (mapsMouseEvent: google.maps.MapMouseEvent) => {
          mapsMouseEvent.stop();
          const lat = mapsMouseEvent.latLng?.lat();
          const lng = mapsMouseEvent.latLng?.lng();

          if (lat && lng) {
            const place = await googlePlaceService.geocodeCoordinates(lat, lng);

            if (place.geometry && place.geometry.location) {
              place.geometry.location.lat = () => lat;
              place.geometry.location.lng = () => lng;
            }

            if (!place.formatted_address) {
              place.formatted_address = `${lat}, ${lng}`;
            }

            setLocationMarker(place);
          }
        },
      );
    }
  }, [map]);

  const handleNewMarker = () => {
    if (locationMarker && locationMarker.place_id) {
      setNewMarkerModal(true);
    }
  };

  const handleEditMarker = (pointLayer: IPointLayer) => {
    setEditMarker(pointLayer);
    setNewMarkerModal(true);
  };

  const onNewMarkerAdded = (marker: IPointLayer) => {
    setAllMarkers((prev) => [marker, ...prev]);
    setRawMarkers((prev) => (prev ? [marker, ...prev] : [marker]));
    setLocationMarker(null);
    setAddressSearchValue('');
    setOptions([]);
    resetMapToDefault();
  };

  const onUpdateMarkerAdded = (marker: IPointLayer) => {
    setAllMarkers((prev) =>
      prev.map((el) => (el._id === marker._id ? marker : el)),
    );
    setRawMarkers(
      (prev) => prev?.map((el) => (el._id === marker._id ? marker : el)) || [],
    );
    setLocationMarker(null);
    setSelectedMarker(marker);
    setAddressSearchValue('');
    setOptions([]);
    resetMapToDefault();
  };

  const newLocationMarker: IMarker | null = useMemo<IMarker | null>(() => {
    if (!locationMarker) return null;

    const cleanAddress = locationMarker.formatted_address?.replace(
      /^[A-Z0-9]{4,}\+[A-Z0-9]{2,},\s*/,
      '',
    );

    return {
      key: String(locationMarker.place_id),
      maxWidth: 400,
      infoWindowFullBg: true,
      info: (
        <Box p={0.5} sx={{ color: 'text.primary' }}>
          <Typography fontWeight={500} fontSize={14} color="inherit">
            {cleanAddress}
          </Typography>
          <Typography fontWeight={400} fontSize={12} color="primary">
            {locationMarker?.geometry?.location?.lat().toFixed(7)} -
            {locationMarker?.geometry?.location?.lng().toFixed(7)}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={handleNewMarker}
            >
              Add marker
            </Button>
          </Box>
        </Box>
      ),
      title: 'New address',
      position: {
        lng: locationMarker?.geometry?.location?.lng() ?? 0,
        lat: locationMarker?.geometry?.location?.lat() ?? 0,
      },
      icon: <IcLocationMarker color={colors.error.main} />,
      showInfo: true,
      toggleInfo: () => {
        setLocationMarker(null);
        setAddressSearchValue('');
        setOptions([]);
        resetMapToDefault();
      },
    };
  }, [locationMarker, handleNewMarker, theme]);

  // Debounced search function
  const debouncedSearch = useDebouncedCallback(
    () => {
      setCurrentPage(1);
      setAllMarkers([]);
      setRawMarkers([]);
      fetchMarkersWithPagination(1, pageLimit, false);
    },
    500, // 500ms debounce delay
  );

  // Fetch markers with pagination for infinite scrolling
  const fetchMarkersWithPagination = useCallback(
    async (
      page: number,
      limit: number,
      append = true,
      overrideFilters?: ILocationMarkerFilter,
    ) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const effectiveFilters = overrideFilters ?? filterValues;

        const filters: Record<string, any> = {
          excludeImported: true,
          isActive: selectedTab === TABS.ACTIVE ? true : false,
          ...(searchValue?.trim() && { search: searchValue.trim() }),
          ...(effectiveFilters.from && { from: effectiveFilters.from }),
          ...(effectiveFilters.to && { to: effectiveFilters.to }),
          ...(effectiveFilters.createdBy && {
            createdBy: effectiveFilters.createdBy,
          }),
          ...(effectiveFilters.markerName && {
            type: effectiveFilters.markerName,
          }),
          ...(effectiveFilters.hasExpiration !== null && {
            hasExpiration: effectiveFilters.hasExpiration,
          }),
        };

        const params: any = {
          page,
          limit,
          ...filters,
        };

        const res = await MapLayerApi.getPointLayers(params);

        if (append && page > 1) {
          setAllMarkers((prev) => [...prev, ...res.data.docs]);
        } else {
          setAllMarkers(res.data.docs);
          setRawMarkers(res.data.docs);
        }

        setHasNextPage(res.data.hasNextPage);
        setCurrentPage(res.data.page);

        // Update map center if this is the first load
        if (map && res.data.docs.length > 0 && !deepLinkMarkerId) {
          const firstMarker = res.data.docs[0];
          map.panTo({
            lng: firstMarker.point.coordinates[0],
            lat: firstMarker.point.coordinates[1],
          });
        }
      } catch (err: any) {
        updateToast({ open: true, message: 'Error loading markers' });
        // Stop further automatic pagination triggers on error to prevent infinite refetch
        if (append) {
          setHasNextPage(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedTab, filterValues, searchValue, updateToast, deepLinkMarkerId],
  );

  // Update map markers based on current allMarkers state
  const updateMapMarkers = useCallback(() => {
    const markerBucket: IMarker[] = [];

    const source = deepLinkMarkerId
      ? allMarkers.filter((m) => m._id === deepLinkMarkerId)
      : allMarkers;

    source.forEach((pointLayer) => {
      const { _id, point, type, properties, updatedAt } = pointLayer;

      const m: IMarker = {
        key: String(_id),
        position: {
          lng: point.coordinates[0],
          lat: point.coordinates[1],
        },
        grayedOut: isExpired(properties?.expirationDateTime),
        isExpired: isExpired(properties?.expirationDateTime),
        icon: getIconForType(type, getMarkerTypeColor(type)),
        type,
        properties,
        updatedAt,
        info: (
          <Stack>
            {pointLayer.isImported && type === PointLayerType.FIRE_HYDRANT ? (
              <FireHydrantPopup data={properties as any} />
            ) : (
              <>
                <Typography fontWeight={500} fontSize={14} sx={{ mt: 1 }}>
                  {PointLayerTypeLabel[type as PointLayerType] || type}
                </Typography>
                {properties?.expirationDateTime && (
                  <Typography color="text.secondary" fontSize={14}>
                    Until:{' '}
                    {formatUnixTimestamp(
                      properties.expirationDateTime,
                      'DD/MM/YYYY HH:mm',
                    )}
                  </Typography>
                )}
                {properties?.description && (
                  <Typography
                    color="text.secondary"
                    fontWeight={400}
                    fontSize={14}
                  >
                    {properties.description}
                  </Typography>
                )}
                {properties?.address && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography color="text.secondary" fontSize={14}>
                      {properties.address}
                    </Typography>
                  </>
                )}
              </>
            )}

            {canEdit() && !pointLayer.isImported && (
              <>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => {
                      handleEditMarker(pointLayer);
                    }}
                    sx={{ minWidth: 'auto', p: '4px' }}
                  >
                    <IcEdit />
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        ),
      };

      markerBucket.push(m);
    });

    setMarkers(markerBucket);
  }, [allMarkers, deepLinkMarkerId, getIconForType, getMarkerTypeColor]);

  // Load more markers callback for infinite scroll
  const loadMoreMarkers = useCallback(() => {
    if (!loadingMore && hasNextPage) {
      fetchMarkersWithPagination(currentPage + 1, pageLimit, true);
    }
  }, [loadingMore, hasNextPage, currentPage, fetchMarkersWithPagination]);

  const handleMarkerClick = (markerKey: string | null) => {
    if (!markerKey || !allMarkers?.length) {
      setSelectedMarker(null);
      return;
    }

    const found = allMarkers?.find((m) => m._id === markerKey);
    if (found) {
      setSelectedMarker(found);
    }
  };

  const goBack = () => {
    setSelectedMarker(null);
    setSelectedMarkerKey(null);
    setAddressSearchValue('');
    setOptions([]);

    fetchMarkersWithPagination(1, pageLimit, false);
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const handleToggleStatus = async () => {
    if (!selectedMarker?._id) return;

    try {
      const expired = isExpired(selectedMarker.properties?.expirationDateTime);

      const { expirationDateTime: _omitExp, ...propertiesWithoutExpiration } =
        selectedMarker.properties || {};

      const updated = await MapLayerApi.updatePointLayer(selectedMarker._id, {
        properties: {
          ...propertiesWithoutExpiration,
          ...(!expired && { expirationDateTime: moment().unix() }),
        },
      });

      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: `Status updated to ${expired ? 'active' : 'inactive'}`,
      });

      const shouldRemove =
        (selectedTab === TABS.ACTIVE && expired) ||
        (selectedTab === TABS.INACTIVE && !expired);
      // Update allMarkers state
      setAllMarkers((prev) =>
        shouldRemove
          ? prev.filter((m) => m._id !== selectedMarker._id)
          : prev.map((m) => (m._id === selectedMarker._id ? { ...m } : m)),
      );

      setRawMarkers((prev) =>
        prev
          ? shouldRemove
            ? prev.filter((m) => m._id !== selectedMarker._id)
            : prev.map((m) => (m._id === selectedMarker._id ? { ...m } : m))
          : [],
      );

      // Update map markers
      updateMapMarkers();
      setSelectedMarker(updated.data);
    } catch (error: any) {
      updateToast({
        open: true,
        message: error.message || 'Failed to update status',
      });
    }
  };

  const hasExpiration = !!selectedMarker?.properties?.expirationDateTime;

  const handleMapBoundsChanged = () => {
    if (map) {
      const bounds = map.getBounds();
      setMapBounds(bounds || null);
    }
  };

  const canEdit = () => {
    if (!user) return false;
    return (
      isTargetRole(user, Role.ACCOUNT_ADMIN) ||
      isTargetRole(user, Role.DISPATCHER)
    );
  };

  const handleFilterChange = (newFilters: ILocationMarkerFilter) => {
    setFilterValues(newFilters);
    setCurrentPage(1);
    setAllMarkers([]);
    setRawMarkers([]);

    fetchMarkersWithPagination(1, pageLimit, false, newFilters);
  };

  // Set up IntersectionObserver for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !loadingMore && !loading) {
          loadMoreMarkers();
        }
      },
      { threshold: 0.1 },
    );

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [hasNextPage, loadingMore, loading, loadMoreMarkers]);

  useEffect(() => {
    setCurrentPage(1);
    setAllMarkers([]);
    setRawMarkers([]);

    fetchMarkersWithPagination(1, pageLimit, false);
  }, [selectedTab]);

  // Initial load when component mounts
  useEffect(() => {
    if (allMarkers.length === 0) {
      fetchMarkersWithPagination(1, pageLimit, false);
    }
  }, []);

  // Update map markers when allMarkers changes
  useEffect(() => {
    updateMapMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMarkers]);

  const placesLibrary = useMapsLibrary('places');

  useEffect(() => {
    if (map) {
      const bounds = map.getBounds();
      if (bounds) {
        setMapBounds(bounds);
      }
    }
  }, [map]);

  const fetchSuggestions = useDebouncedCallback((inputValue: string) => {
    if (!inputValue || !placesLibrary || !mapBounds) return;

    const service = new placesLibrary.AutocompleteService();

    // Create a proper LatLngBounds object for the request
    const bounds = new google.maps.LatLngBounds(
      mapBounds.getSouthWest(),
      mapBounds.getNorthEast(),
    );

    service.getPlacePredictions(
      {
        input: inputValue,
        types: ['address'],
        locationRestriction: bounds, // Use locationRestriction instead of bounds + strictBounds
        componentRestrictions: {
          // Keep country restriction
          country: 'us',
        },
      },
      (predictions) => {
        setOptions(predictions || []);
      },
    );
  }, 300);

  return (
    <Page
      className="relative"
      title="Location Markers"
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        '& .MuiBox-root': { padding: 0 },
      }}
      style={{ padding: 0 }}
    >
      <div
        onClick={handleNavigateBack}
        className="absolute z-50 p-1 text-red-500 bg-white border border-gray-400 rounded-md cursor-pointer left-3 top-3"
      >
        <ChevronLeft />
      </div>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* LEFT SIDE - Map Container */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'background.default',
          }}
        >
          <GoogleMap
            defaultCenter={defaultMapCenter}
            defaultZoom={12}
            markers={[
              ...(markers || []),
              ...(newLocationMarker ? [newLocationMarker] : []),
            ]}
            showContextMenu={true}
            showStreetViewControl={false}
            mapTypeControl={false}
            showZoomControl={false}
            style={{
              height: '100%',
              width: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            zoom={12}
            onMarkerClick={handleMarkerClick}
            onBoundsChanged={handleMapBoundsChanged}
            selectedMarkerKey={selectedMarkerKey}
            onCloseMarkerInfo={goBack}
          >
            {/* Address Search Field - Positioned OVER the map */}
            <MapControl position={ControlPosition.TOP_CENTER}>
              <Box
                sx={{
                  mt: 2,
                  ml: 2,
                  width: '400px',
                  borderRadius: '8px',
                  boxShadow:
                    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                }}
              >
                <Autocomplete
                  forcePopupIcon={false}
                  clearIcon={null}
                  filterOptions={(x) => x}
                  options={options}
                  value={null}
                  inputValue={addressSearchValue}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.description
                  }
                  autoComplete
                  includeInputInList
                  noOptionsText="No addresses found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Search address..."
                      variant="outlined"
                      size="medium"
                      sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '6px',
                        padding: '10px',
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: addressSearchValue ? (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setAddressSearchValue('');
                                setLocationMarker(null);
                                setOptions([]);
                                resetMapToDefault();
                              }}
                              size="small"
                              edge="end"
                              sx={{ mr: 0 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  )}
                  onInputChange={(_, newInputValue, reason) => {
                    // Only update the search text if the user is typing
                    // or if the reset comes from the clear button.
                    if (reason === 'input' || reason === 'clear') {
                      setAddressSearchValue(newInputValue);
                    }

                    if (
                      reason === 'clear' ||
                      (reason === 'input' && newInputValue === '')
                    ) {
                      setLocationMarker(null);
                      setOptions([]);
                      resetMapToDefault();
                    }

                    if (newInputValue && reason === 'input') {
                      fetchSuggestions(newInputValue);
                    }
                  }}
                  onChange={async (_, newValue) => {
                    if (newValue && window.google) {
                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode(
                        { placeId: newValue.place_id },
                        (results, status) => {
                          if (
                            status === 'OK' &&
                            results?.[0]?.geometry?.location
                          ) {
                            const location = results[0].geometry.location;
                            const lat = location.lat();
                            const lng = location.lng();

                            if (map) {
                              map.panTo({ lat, lng });
                              map.setZoom(17);
                            }

                            setTimeout(() => {
                              const newPlace: google.maps.places.PlaceResult = {
                                place_id: newValue.place_id,
                                formatted_address: results[0].formatted_address,
                                geometry: {
                                  location: {
                                    lat: () => lat,
                                    lng: () => lng,
                                  } as any,
                                },
                              };

                              setLocationMarker(newPlace);
                              setAddressSearchValue(
                                results[0].formatted_address || '',
                              );
                              setOptions([]);
                            }, 300);
                          }
                        },
                      );
                    }
                  }}
                />
              </Box>
            </MapControl>

            <MapTypeControl />
          </GoogleMap>
        </Box>

        {/* RIGHT SIDE - Sidebar */}
        <Box
          sx={{
            width: '450px',
            height: '100%',
            flexShrink: 0, // FIX 4: Prevent sidebar from being squished by the map
            backgroundColor: theme.palette.background.paper,
            borderLeft: '1px solid #E0E0E0',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 5,
          }}
        >
          {!selectedMarker && !deepLinkMarkerId && (
            <>
              <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search Location markers..."
                  variant="outlined"
                  size="small"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    debouncedSearch();
                  }}
                  InputProps={{
                    endAdornment: searchValue ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setSearchValue('');
                            debouncedSearch();
                          }}
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Stack>

              <Divider sx={{ mt: 2 }} />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Box flex={1}>
                  <Tabs
                    value={selectedTab}
                    onChange={(_, newValue) => setSelectedTab(newValue)}
                    variant="fullWidth"
                    sx={{ minHeight: 'unset', height: 60 }}
                  >
                    {TABS_BY_PAGE.map((tab, index) => (
                      <StyledTab
                        key={index}
                        label={tab.label}
                        value={tab.path}
                      />
                    ))}
                  </Tabs>
                </Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: '#E0E0E0' }}
                />
                <Box sx={{ ml: 2 }}>
                  <LocationMarkerFilterMenu
                    filterValues={filterValues}
                    onChangeFilter={handleFilterChange}
                  />
                </Box>
              </Stack>

              <Box sx={{ flex: 1, overflowY: 'auto', padding: 2 }}>
                <Stack spacing={2}>
                  {!loading &&
                    allMarkers?.map((marker, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          padding: 2,
                          boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
                          border: '1px solid #E0E0E0',
                        }}
                        onClick={() => {
                          handleMarkerClick(marker?._id as string);
                          setSelectedMarkerKey(marker?._id as string);
                        }}
                      >
                        <Box fontWeight={600} fontSize="16px" mb={2}>
                          {PointLayerTypeLabel[marker.type as PointLayerType] ||
                            marker.type}
                        </Box>

                        <Divider />

                        <Box
                          fontWeight={600}
                          fontSize={14}
                          color="text.primary"
                          mt={2}
                          mb={1}
                        >
                          {marker.properties?.location ??
                            marker.properties?.address ??
                            'Unknown Address'}
                        </Box>

                        <Box fontSize="14px" color="#5F6A7D" mb={2}>
                          {marker.properties?.description ||
                            'No description provided.'}
                        </Box>

                        <Divider />

                        <Stack
                          direction="row"
                          spacing={3}
                          alignItems="center"
                          mt={2}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <IcCalendar />
                            <Box fontSize="14px">
                              {marker.updatedAt
                                ? moment(marker.updatedAt).format(
                                    'MM/DD/YYYY HH:mm',
                                  )
                                : 'Unknown time'}
                            </Box>
                          </Stack>

                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ borderColor: '#E0E0E0' }}
                          />

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <IcPersonCreated />
                            <Box fontSize="14px">
                              {typeof marker?.createdBy === 'object'
                                ? formatPersonName(marker.createdBy) || '-'
                                : marker?.createdBy || '-'}
                            </Box>
                          </Stack>
                        </Stack>
                      </Box>
                    ))}

                  {/* Infinite scroll trigger */}
                  <div ref={observerRef} style={{ height: '20px' }} />

                  {/* Loading indicators */}
                  {loading && (
                    <Box display="flex" justifyContent="center">
                      <CircularProgress />
                    </Box>
                  )}

                  {loadingMore && (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  {/* End of data indicator */}
                  {!loading &&
                    !loadingMore &&
                    !hasNextPage &&
                    (rawMarkers?.length || 0) > 0 && (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No more markers to load
                        </Typography>
                      </Box>
                    )}
                </Stack>
              </Box>
            </>
          )}
          {selectedMarker && (
            <Stack
              direction="column"
              justifyContent="space-between"
              sx={{
                height: '100%',
                overflow: 'hidden',
                padding: 2,
              }}
            >
              <Box
                sx={{
                  overflowY: 'auto',
                  paddingBottom: 2,
                  overflow: 'hidden',
                }}
              >
                <Button variant="text" color="secondary" onClick={goBack}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <IcArrowLeft />
                    <Typography
                      fontSize={18}
                      fontWeight={500}
                      color={theme.palette.text.secondary}
                    >
                      Back
                    </Typography>
                  </Stack>
                </Button>
              </Box>

              <Divider />
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ padding: 2 }}>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    {selectedMarker.type ?? '-'}
                  </Typography>

                  <Typography color="text.secondary" fontWeight={400} mb={1}>
                    {selectedMarker.properties?.location ??
                      selectedMarker?.properties?.address ??
                      ''}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ padding: 2 }}>
                  <Typography fontWeight={600} mb={1}>
                    Description
                  </Typography>

                  <Typography color="text.secondary" fontSize={14}>
                    {selectedMarker.properties?.description ??
                      'No description provided.'}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack
                  direction="row"
                  divider={<Divider orientation="vertical" flexItem />}
                  spacing={3}
                  alignItems="flex-start"
                  sx={{ paddingLeft: 2 }}
                >
                  <Box flex={1}>
                    <Typography fontSize={14} variant="h6">
                      Date created
                    </Typography>
                    <Typography fontSize={14}>
                      {selectedMarker.updatedAt
                        ? moment(selectedMarker.updatedAt).format(
                            'HH:mm:ss - DD/MM/YYYY',
                          )
                        : '-'}
                    </Typography>
                  </Box>

                  <Box flex={1}>
                    <Typography fontSize={14} variant="h6">
                      Created by
                    </Typography>
                    <Typography fontSize={14}>
                      {typeof selectedMarker.createdBy === 'object'
                        ? formatPersonName(selectedMarker.createdBy) || '-'
                        : '-'}
                    </Typography>
                  </Box>

                  {hasExpiration && (
                    <Box flex={1}>
                      <Typography fontSize={14} variant="h6">
                        Expiration date
                      </Typography>
                      <Typography fontSize={14}>
                        {selectedMarker.properties?.expirationDateTime
                          ? formatUnixTimestamp(
                              selectedMarker.properties.expirationDateTime,
                              'HH:mm - DD/MM/YYYY',
                            )
                          : '-'}
                      </Typography>
                    </Box>
                  )}

                  <Box flex={1}>
                    <Typography fontSize={14} variant="h6">
                      ID
                    </Typography>
                    <Typography fontSize={14}>
                      {selectedMarker.uniqueId || selectedMarker._id}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Fixed button at the bottom */}
              {canEdit() && (
                <Box sx={{ padding: 2, borderTop: '1px solid #E0E0E0' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={selectedMarker.isImported}
                    sx={{
                      borderRadius: 2,
                      backgroundColor:
                        selectedMarker.status === 'ACTIVE'
                          ? 'error.main'
                          : '#6DCCC4',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor:
                          selectedMarker.status === 'ACTIVE'
                            ? '#d32f2f'
                            : '#5dbbb3',
                      },
                    }}
                    onClick={() => handleToggleStatus()}
                  >
                    {`Move to ${
                      isExpired(selectedMarker.properties?.expirationDateTime)
                        ? 'active'
                        : 'inactive'
                    }`}
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <CreateNewMarkerModal
        place={locationMarker || undefined}
        open={newMarkerModal}
        marker={editMarker as IPointLayer}
        onNewMarker={onNewMarkerAdded}
        onUpdateMarker={onUpdateMarkerAdded}
        onClose={() => {
          setNewMarkerModal(false);
          setLocationMarker(null);
          setEditMarker(null);
          setAddressSearchValue('');
        }}
      />
    </Page>
  );
};

export default withLoader(LocationMarkers);
